import { Bell, Mail, MessageSquare } from 'lucide-react';
import type { NotificationLogRow } from '@techsofit/api-types';
import { Card, EmptyState, Skeleton, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { useAsync } from '../lib/useAsync';
import { getNotifications } from '../lib/gymApi';

const EVENT_LABELS: Record<string, string> = {
  MEMBERSHIP_EXPIRING: 'Membership expiring',
  MEMBERSHIP_EXPIRED: 'Membership expired',
  MEMBERSHIP_WINBACK: 'Win-back',
  GOAL_CHECKIN: 'Goal check-in',
  SUBSCRIPTION_EXPIRING: 'Subscription expiring',
  SUBSCRIPTION_PAST_DUE: 'Subscription past due',
  PAYMENT_RECEIPT: 'Payment receipt',
  WORKOUT_READY: 'Workout ready',
  GUEST_PASS: 'Guest pass',
};

const eventLabel = (e: string) => EVENT_LABELS[e] ?? e.replace(/_/g, ' ').toLowerCase();

function when(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

export function NotificationsPage() {
  const { data, loading } = useAsync(() => getNotifications(100));
  const rows = data ?? [];

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow="Messaging"
        title="Notifications sent"
        description="Every automatic reminder we've sent your members and you — proof it's working. SMS goes to members on an SMS plan; everyone else gets email."
      />

      <Card className="overflow-hidden p-0">
        {loading ? (
          <div className="flex flex-col gap-2 p-4">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-12" />
            ))}
          </div>
        ) : rows.length ? (
          <ul className="divide-y divide-line">
            {rows.map((r) => (
              <NotificationRow key={r.id} row={r} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Bell className="size-6" aria-hidden />}
            title="Nothing sent yet"
            description="Reminders will appear here as they go out."
            className="rounded-none shadow-none"
          />
        )}
      </Card>
    </section>
  );
}

function NotificationRow({ row }: { row: NotificationLogRow }) {
  const isSms = row.channel === 'SMS';
  const sent = row.status === 'SENT';
  return (
    <li className="flex items-center gap-3 px-4 py-3 md:px-5">
      <span
        className={cn(
          'grid size-9 shrink-0 place-items-center rounded-control',
          isSms ? 'bg-accent-soft text-accent-ink' : 'bg-surface text-ink-soft',
        )}
      >
        {isSms ? <MessageSquare className="size-4" aria-hidden /> : <Mail className="size-4" aria-hidden />}
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium text-ink">{eventLabel(row.event)}</p>
        <p className="truncate text-sm text-muted">
          {row.channel === 'SMS' ? 'SMS' : 'Email'} · {row.destination ?? '—'}
        </p>
      </div>
      {!sent && (
        <span
          className={cn(
            'hidden shrink-0 rounded-pill px-2.5 py-1 text-xs font-medium sm:inline',
            row.status === 'FAILED' ? 'bg-bad-soft text-bad-ink' : 'bg-surface text-ink-soft',
          )}
        >
          {row.status === 'FAILED' ? 'Failed' : 'Skipped'}
        </span>
      )}
      <span className="shrink-0 text-xs text-muted tabular">{when(row.createdAt)}</span>
    </li>
  );
}
